import {useObvioUser} from 'obvio/auth'
import {
  api,
  queryParser,
  useQueryParams,
  useSetBulkQueryParam,
  useSetQueryParam,
} from 'lib/url'
import {teamMemberClient} from 'obvio/obvio-client'
import {Organization} from 'organization'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {sendRequest} from 'obvio/Organizations/CreateOrganizationForm'
import {PaginatedCollection} from 'lib/ui/api-client'
export type PerPageOption = 12 | 24 | 48 | 100

const DEFAULT_PER_PAGE: PerPageOption = 12
const PER_PAGE_OPTIONS: PerPageOption[] = [12, 24, 48, 100]

interface UserOrganizationsContextProps {
  organizations: Organization[]
  loading: boolean
  searchQuery: string
  setSearchQuery: (query: string) => void
  page: number
  setPage: (page: number) => void
  perPage: PerPageOption
  setPerPage: (perPage: PerPageOption) => void
  lastPage: number
  error: string | null
  clearError: () => void
}

const UserOrganizationsContext = React.createContext<
  UserOrganizationsContextProps | undefined
>(undefined)

export default function UserOrganizationsProvider(props: {
  children: React.ReactNode
}) {
  const url = useListUrlParams()

  const [organizations, setOrganizations] = useState<Organization[] | null>(
    null,
  )

  const [lastPage, setLastPage] = useState(1)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const clearError = () => setError(null)

  const setQueryParam = useSetQueryParam()
  const setBulkQueryParam = useSetBulkQueryParam()

  const {
    has_active_subscription: isSubscribed,
    first_name,
    last_name,
  } = useObvioUser()

  const newOrganizationName = `${first_name} ${last_name}'s Organization`

  const setSearchQuery = (query: string) =>
    setBulkQueryParam({search: query, page: '1'})

  const setPage = (page: number) => setQueryParam('page', `${page}`)

  const setPerPage = (perPage: PerPageOption) =>
    setBulkQueryParam({per_page: `${perPage}`, page: '1'})

  const createOrganization = useCallback(() => {
    const orgData = {
      name: newOrganizationName,
      logo: null,
    }

    sendRequest(orgData)
      .then((data) => {
        setOrganizations([data])
      })
      .catch((error) => {
        console.error(error)
      })
  }, [newOrganizationName])

  useEffect(() => {
    setLoading(true)
    loadOrganizations(url.page, url.per_page, url.search)
      .then((organizations) => {
        // Auto-create an organization if a user is subscribed, and they
        // haven't created on already.
        const shouldAutoCreateOrganization =
          isSubscribed && organizations.total === 0 && !url.search
        if (shouldAutoCreateOrganization) {
          createOrganization()
          return
        }

        clearError()
        setOrganizations(organizations.data)
        setLastPage(organizations.last_page)
      })
      .catch(() => {
        setError('An error occurred while fetching organizations')
        setOrganizations([])
      })
      .finally(() => {
        setLoading(false)
      })
  }, [createOrganization, isSubscribed, url.page, url.per_page, url.search])

  return (
    <UserOrganizationsContext.Provider
      value={{
        loading: organizations === null || loading,
        organizations: organizations || [],
        searchQuery: url.search,
        setSearchQuery,
        page: url.page,
        setPage,
        perPage: url.per_page,
        setPerPage,
        lastPage,
        error,
        clearError,
      }}
    >
      {props.children}
    </UserOrganizationsContext.Provider>
  )
}

export function useUserOrganizations() {
  const context = React.useContext(UserOrganizationsContext)
  if (context === undefined) {
    throw new Error(
      `useUserOrganizations must be used within an UserOrganizationsProvier`,
    )
  }

  return context
}

function useListUrlParams() {
  const queryParams = useQueryParams()
  return useMemo(() => parseListUrlParams(queryParams), [queryParams])
}

export function parseListUrlParams(params: Record<string, any>) {
  const parse = queryParser(params)

  const rawPerPage = parse.number('per_page')
  const perPage =
    rawPerPage && PER_PAGE_OPTIONS.includes(rawPerPage as PerPageOption)
      ? (rawPerPage as PerPageOption)
      : DEFAULT_PER_PAGE

  return {
    search: parse.string('search') || '',
    page: parse.number('page') || 1,
    per_page: perPage,
  }
}

function loadOrganizations(
  page: number,
  perPage: PerPageOption,
  searchQuery: string,
) {
  const params = new URLSearchParams()
  params.set('page', `${page}`)
  params.set('per_page', `${perPage}`)
  const trimmed = searchQuery.trim()
  if (trimmed) {
    params.set('search', trimmed)
  }

  return teamMemberClient.get<PaginatedCollection<Organization>>(
    api(`/organizations?${params.toString()}`),
  )
}
